import React, { useState, useEffect, useRef } from 'react';
import AdSense from 'react-adsense';
import adConfig from '../services/adConfig';

// AdContainer component for displaying both static and video ads
function AdContainer({ 
  adType = 'display', // 'display' or 'video'
  onClose, // Callback when ad is skipped
  duration = 15, // Duration in seconds for video ads
  format = 'fullscreen', // 'fullscreen', 'banner', or 'interstitial'
  testMode = true, // Use test ads or real ads
}) {
  const [countdown, setCountdown] = useState(duration);
  const [canSkip, setCanSkip] = useState(false);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adError, setAdError] = useState(false);
  const videoRef = useRef(null);
  const countdownInterval = useRef(null);
  
  // Get ad units from configuration
  const getAdUnits = () => {
    const units = testMode ? adConfig.testUnits : adConfig.productionUnits;

    if (adType === 'display') {
      switch(format) {
        case 'banner':
          return units.banner;
        case 'interstitial':
          return units.interstitial;
        case 'fullscreen':
        default:
          return units.fullscreen;
      }
    } else { // video
      return format === 'fullscreen' ? units.videoInterstitial : units.reward;
    }
  };

  const adUnits = getAdUnits();

  // Initialize countdown timer for video ads
  useEffect(() => {
    if (adType === 'video' && adLoaded) {
      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current);
            setCanSkip(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [adType, adLoaded]);

  // Handle video ad loading
  const handleVideoLoad = () => {
    setAdLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.error('Error playing video ad:', err);
        setAdError(true);
      });
    }
  };

  // Handle ad errors
  const handleAdError = (error) => {
    console.error('Ad failed to load:', error);
    setAdError(true);
    // Fallback to a different ad or close
    if (onClose) {
      setTimeout(onClose, 1000);
    }
  };

  // Handle skip button click
  const handleSkip = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (onClose) {
      onClose();
    }
  };

  // Determine container style based on format
  const getContainerStyle = () => {
    switch (format) {
      case 'fullscreen':
        return {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          maxWidth: '600px', // Match the max-width of the app container
          margin: '0 auto', // Center the ad in the viewport
          zIndex: 1000,
          backgroundColor: '#111',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        };
      case 'banner':
        return {
          width: '100%',
          maxWidth: '600px', // Match the max-width of the app container
          maxHeight: '120px',
          backgroundColor: '#111',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          overflow: 'hidden',
          margin: '15px auto', // Center horizontally
          boxSizing: 'border-box',
        };
      case 'interstitial':
        return {
          position: 'relative',
          width: '100%',
          maxWidth: '600px', // Match the max-width of the app container
          minHeight: '250px',
          maxHeight: '400px',
          backgroundColor: '#111',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '12px',
          overflow: 'hidden',
          margin: '10px auto', // Center horizontally
          border: '1px solid rgba(255,255,255,0.1)',
          boxSizing: 'border-box',
        };
      default:
        return {
          width: '100%',
          maxWidth: '600px', // Match the max-width of the app container
          minHeight: '250px',
          backgroundColor: '#111',
          margin: '0 auto',
          boxSizing: 'border-box',
        };
    }
  };

  // Show different content based on ad type
  return (
    <div style={getContainerStyle()}>
      {/* Ad loading indicator or error message */}
      {!adLoaded && !adError && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 2,
          color: 'white',
          fontSize: '18px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{
              width: '40px',
              height: '40px',
              margin: '0 auto 15px',
              border: '4px solid rgba(255,255,255,0.3)',
              borderRadius: '50%',
              borderTop: '4px solid #FF6B00',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div>Loading Ad...</div>
          </div>
        </div>
      )}

      {/* Error display */}
      {adError && (
        <div style={{ 
          color: 'white', 
          padding: '20px', 
          textAlign: 'center' 
        }}>
          <p>There was an error loading this ad.</p>
          <button 
            onClick={handleSkip} 
            style={{
              backgroundColor: '#FF6B00', 
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '20px',
              marginTop: '10px',
              cursor: 'pointer'
            }}
          >
            Continue
          </button>
        </div>
      )}

      {/* Display ad content */}
      {adType === 'display' && !adError && (
        <div style={{ width: '100%', height: '100%', position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
          <AdSense.Google
            client={adUnits.adClient}
            slot={adUnits.adSlot}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              maxWidth: format === 'banner' ? '320px' : '100%',
              overflow: 'hidden'
            }}
            format={format === 'banner' ? 'auto' : ''}
            responsive='true'
            onLoad={() => setAdLoaded(true)}
            onError={handleAdError}
          />
          
          {/* Skip button for display ads */}
          {format !== 'banner' && (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 10
            }}>
              <button 
                onClick={handleSkip}
                style={{
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '20px',
                  padding: '5px 12px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Skip Ad
              </button>
            </div>
          )}
          
          {/* Ad label */}
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: 'rgba(255,255,255,0.7)',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            Ad
          </div>
        </div>
      )}

      {/* Video ad content */}
      {adType === 'video' && !adError && (
        <div style={{ width: '100%', height: '100%', position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
          <video
            ref={videoRef}
            src={adUnits.testVideoUrl || adConfig.testUnits.videoInterstitial.testVideoUrl}
            style={{
              width: '100%',
              height: format === 'fullscreen' ? '100%' : 'auto',
              maxHeight: format === 'fullscreen' ? '100%' : '400px',
              objectFit: 'contain',
              borderRadius: format === 'fullscreen' ? '0' : '8px'
            }}
            onLoadedData={handleVideoLoad}
            onError={handleAdError}
            onEnded={() => {
              setCanSkip(true);
              setCountdown(0);
            }}
            controls={false}
            muted={false}
            playsInline
          />
          
          {/* Video ad countdown/skip UI */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '10px',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {!canSkip ? (
              <>
                <div style={{ fontSize: '14px' }}>Skip in {countdown}s</div>
                <div style={{ 
                  width: '20px', 
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </>
            ) : (
              <button 
                onClick={handleSkip}
                style={{
                  backgroundColor: '#FF6B00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Skip Ad
              </button>
            )}
          </div>
          
          {/* Ad label */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: 'rgba(255,255,255,0.7)',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            Video Ad
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default AdContainer;